import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import WikiPaywall from "../components/WikiPaywall";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import "../styles/wiki.css";

export default function WikiArticle() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const hasAccess = !!user?.wiki_access;

  useEffect(() => {
    if (!hasAccess) return;
    setArticle(null);
    setNotFound(false);
    api
      .get(`/wiki/article/${slug}?lang=${lang}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug, lang, hasAccess]);

  useEffect(() => {
    if (article?.title) document.title = `${article.title} · ${t("title.suffix")}`;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [article, t]);

  if (!hasAccess) return <WikiPaywall />;

  if (notFound)
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif font-bold text-3xl text-slate-900">{t("wiki.notFound")}</h1>
        <Link to="/wiki" className="mt-4 inline-block text-teal-700 hover:underline">{t("wiki.backToWiki")}</Link>
      </div>
    );

  if (!article)
    return <div className="min-h-[60vh] grid place-items-center text-slate-400 font-mono text-sm">{t("wiki.loading")}</div>;

  const color = article.color || "#17324d";
  const domain = article.domain;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="wiki-article-page">
      <Link to="/wiki" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeft size={16} /> {t("wiki.backToWiki")}
      </Link>

      <div className="grid lg:grid-cols-[260px_minmax(0,1fr)] gap-10">
        {/* Sidebar: domain articles */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-slate-100" style={{ background: article.soft || "#f8f7f3" }}>
              <div className="font-serif font-medium text-2xl" style={{ color }}>{domain?.number}</div>
              <div className="mt-1 font-serif font-bold text-slate-900 leading-snug">{domain?.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{domain?.subtitle}</div>
            </div>
            <nav className="p-2" data-testid="wiki-domain-nav">
              {domain?.articles?.map((a) => {
                const active = a.slug === article.slug;
                return (
                  <Link
                    key={a.slug}
                    to={`/wiki/${a.slug}`}
                    data-testid={`wiki-nav-${a.slug}`}
                    aria-current={active ? "page" : undefined}
                    className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
                      active ? "font-semibold text-white" : "text-slate-600 hover:bg-slate-50"
                    }`}
                    style={active ? { background: color } : undefined}
                  >
                    {a.title}
                  </Link>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Article */}
        <motion.article
          key={article.slug}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="min-w-0"
        >
          <div
            className="rounded-2xl border p-8 sm:p-10 mb-8"
            style={{ borderColor: `${color}33`, background: `linear-gradient(135deg, ${article.soft || "#f8f7f3"}, #fff)` }}
          >
            <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest mb-3" style={{ color }}>
              <BookOpen size={13} /> {domain?.title}
            </div>
            <h1 className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-[1.1]">
              {article.title}
            </h1>
            {article.summary && <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-2xl">{article.summary}</p>}
          </div>

          <div className="wiki-article" dangerouslySetInnerHTML={{ __html: article.body }} />

          {/* Prev / Next */}
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            {article.prev ? (
              <Link
                to={`/wiki/${article.prev.slug}`}
                data-testid="wiki-prev-link"
                className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-300 transition-colors"
              >
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                  <ArrowLeft size={12} /> {t("wiki.prev")}
                </span>
                <span className="mt-1 font-serif font-semibold text-slate-900">{article.prev.title}</span>
              </Link>
            ) : (
              <span />
            )}
            {article.next && (
              <Link
                to={`/wiki/${article.next.slug}`}
                data-testid="wiki-next-link"
                className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-300 transition-colors sm:text-right"
              >
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1 sm:justify-end">
                  {t("wiki.next")} <ArrowRight size={12} />
                </span>
                <span className="mt-1 font-serif font-semibold text-slate-900">{article.next.title}</span>
              </Link>
            )}
          </div>
        </motion.article>
      </div>
    </div>
  );
}
